import type { PropsWithChildren, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ComplexTable,
  ComplexTableRow,
  TableCell,
  TableHeadRow
} from '../molecules';
import styled, { css } from 'styled-components';
import type { TTableCellContent } from '../atoms';
import {
  InputWrapper,
  PrimaryAltIconButton,
  TableCellContent,
  TransparentIconButton
} from '../atoms';
import type {
  ArrayPath,
  Control,
  FieldArray,
  FieldArrayWithId,
  Path,
  UseFormRegister
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import type { CellType, ColumnType } from '../../common';
import { mapCellTypeToInputType } from '../../common';
import omit from 'lodash/omit';
import { cloneDeep } from 'lodash';

const CONTENT_TYPE_MAPPING = {
  range: 'rangeCurrency',
  number: 'currency'
} as const;

export interface EditableTableProps<
  Value extends Array<Record<ColumnType['name'], CellType['content']>>,
  Schema extends z.ZodTypeAny
> {
  errorMessage?: string;
  control: Control<Schema>;
  name: ArrayPath<Schema>;
  register: UseFormRegister<Schema>;
  hideDeleteButton?: boolean;
  disabledAdding?: boolean;
  getValues: UseFormGetValues<Schema>;
  getTableCellSpan?: (row: Value[number], index: number) => number;
  title?: string;
  // onChange: (value: Value) => void;
  getDefaultValue?: (
    value: Array<FieldArrayWithId<Schema, ArrayPath<Schema>, 'id'>>
  ) => FieldArray<Schema, ArrayPath<Schema>>;
}

export const ActionsWrapper = styled.span(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.margins.xs};
    justify-content: start;
    align-items: center;
  `
);

const TableContainer = styled.div<{ scrollState: string }>`
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  margin-top: ${({ theme }) => theme.margins.md};

  table {
    width: 100%;
    border-collapse: collapse;

    thead {
      background-color: ${({ theme }) => theme.colors.white};
    }

    tr {
      > *:first-of-type,
      > *:last-of-type:not(:first-of-type) {
        position: sticky;

        &::after {
          content: '';
          position: absolute;
          display: block;
          inset: 0;
          z-index: -1;
          background-color: ${({ theme }) => theme.colors.white};
        }
      }

      > *:first-of-type {
        left: 0;
        z-index: 2;

        &::after {
          box-shadow: ${({ scrollState }) =>
            scrollState.includes('scroll-left')
              ? 'rgba(0, 0, 0, 0.15) 3px 0px 3px 0px'
              : 'none'};
        }
      }

      > *:last-of-type:not(:first-of-type) {
        right: 0;
        z-index: 1; // To make it appear below the first sticky column
        &::after {
          box-shadow: ${({ scrollState }) =>
            scrollState.includes('scroll-right')
              ? 'rgba(0, 0, 0, 0.15) -3px 0px 3px 0px'
              : 'none'};
        }
      }
    }
  }
`;

function EditableTable<
  Value extends Array<Record<ColumnType['name'], CellType['content']>>,
  Schema extends z.ZodTypeAny
>({
  control,
  name,
  register,
  getDefaultValue,
  children,
  getValues,
  hideDeleteButton,
  getTableCellSpan = () => 1,
  title = ''
}: PropsWithChildren<EditableTableProps<Value, Schema>>) {
  const [editingRow, setEditingRow] = useState(-1);
  const [editingColumn, setEditingColumn] = useState(-1);

  const [rollbackData, setRollbackData] =
    useState<FieldArrayWithId<Schema, ArrayPath<Schema>>>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name
  });
  const [scrollState, setScrollState] = useState<string>('');
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tableRef = tableContainerRef.current;
    const handleScroll = () => {
      if (!tableRef) return;

      const el = tableRef;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;

      let newScrollState = '';
      if (el.scrollLeft > 0) newScrollState += 'scroll-left ';
      if (el.scrollLeft < maxScrollLeft) newScrollState += 'scroll-right';

      setScrollState(newScrollState.trim());
    };

    handleScroll();

    tableRef?.addEventListener('scroll', handleScroll);
    return () => {
      tableRef?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const saveRow = (rollback = false) => {
    if (rollback && rollbackData) {
      const data = (
        'id' in rollbackData
          ? Object.values(omit(rollbackData, ['id', 'added']))
          : rollbackData
      ) as FieldArray<Schema, ArrayPath<Schema>>;

      update(editingRow, data);
    }

    setEditingRow(-1);
    setEditingColumn(-1);
    setRollbackData(undefined);
  };

  const deleteRow = (index: number) => {
    remove(index);
    setEditingRow(-1);
    setEditingColumn(-1);
    setRollbackData(undefined);
  };

  const addRow = () => {
    if (getDefaultValue) {
      append(getDefaultValue(fields));
      setRollbackData(undefined);
      setEditingRow(-1);
      setEditingColumn(-1);
    }
  };

  const startEditing = (index: number) => {
    setRollbackData(cloneDeep(fields[index]));
    setEditingRow(index);
  };

  let header = null;
  let headerActions = null;
  let preBodyRow = null;

  React.Children.forEach(children, (child, index) => {
    if (React.isValidElement(child)) {
      switch (child.type) {
        case EditableTable.HeaderActions:
          headerActions = child;
          break;
        case EditableTable.TableHeader:
          header = child;
          break;
        case EditableTable.PreBodyRow:
          preBodyRow = child;
          break;
        default:
          break;
      }
    }
  });

  return (
    <div>
      {Boolean(title || headerActions || getDefaultValue) && (
        <EditableTable.PreHeader>
          <EditableTable.Title>{title}</EditableTable.Title>
          <EditableTable.Actions>
            {headerActions}
            {getDefaultValue && (
              <PrimaryAltIconButton type="button" onClick={() => addRow()}>
                <span>Add Row</span>
                <span className="material-symbols-rounded">add</span>
              </PrimaryAltIconButton>
            )}
          </EditableTable.Actions>
        </EditableTable.PreHeader>
      )}

      <TableContainer ref={tableContainerRef} scrollState={scrollState}>
        <ComplexTable>
          {header}
          {fields.length !== 0 && (
            <tbody>
              {preBodyRow}
              {fields.map((row, index) => (
                <ComplexTableRow key={row.id}>
                  {Object.values(omit(row, ['id', 'added'])).map(
                    (_cell, colIndex: number) => {
                      const cell = _cell as TTableCellContent;
                      const editingType = mapCellTypeToInputType(cell);
                      const contentPath =
                        `${name}.${index}.${colIndex}.content` as Path<Schema>;

                      const contentType =
                        cell.contentType in CONTENT_TYPE_MAPPING
                          ? CONTENT_TYPE_MAPPING[
                              cell.contentType as keyof typeof CONTENT_TYPE_MAPPING
                            ]
                          : cell.contentType;

                      if (index === editingRow && !_cell.nonEditable) {
                        return (
                          <TableCell
                            key={uuidv4()}
                            reducePadding={true}
                            colSpan={getTableCellSpan(row, colIndex)}
                          >
                            <InputWrapper
                              name={contentPath}
                              type={editingType as any}
                              inputSize={'sm'}
                              step={0.01}
                              register={(name, options) =>
                                register(name, {
                                  ...options,
                                  // re-focus after re-render
                                  onChange: (e) => {
                                    setEditingColumn(colIndex);
                                    options?.onChange?.(e);
                                  },
                                  onBlur: (e) => {
                                    setEditingColumn(-1);
                                    options?.onBlur?.(e);
                                  }
                                })
                              }
                              removeMargin={true}
                              autoFocus={colIndex === editingColumn}
                            />
                          </TableCell>
                        );
                      }

                      return (
                        <EditableTable.Cell
                          key={uuidv4()}
                          colSpan={getTableCellSpan(row, colIndex)}
                        >
                          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                          {/*@ts-ignore*/}
                          <TableCellContent
                            content={getValues(contentPath)}
                            contentType={contentType}
                            options={
                              cell && cell.options ? cell.options : undefined
                            }
                          />
                        </EditableTable.Cell>
                      );
                    }
                  )}
                  <EditableTable.RowActions key={index}>
                    {editingRow !== index ? (
                      <ActionsWrapper>
                        <TransparentIconButton
                          type="button"
                          onClick={() => startEditing(index)}
                        >
                          <span className="material-symbols-rounded">edit</span>
                        </TransparentIconButton>
                        {!hideDeleteButton && (
                          <TransparentIconButton
                            type="button"
                            onClick={() => deleteRow(index)}
                          >
                            <span className="material-symbols-rounded">
                              delete
                            </span>
                          </TransparentIconButton>
                        )}
                      </ActionsWrapper>
                    ) : (
                      <ActionsWrapper>
                        <TransparentIconButton
                          larger
                          type="button"
                          style={{ color: 'royalblue' }}
                          title={'Roll back edits made to this row.'}
                          onClick={() => saveRow(true)}
                        >
                          <span className="material-symbols-rounded">undo</span>
                        </TransparentIconButton>
                        <TransparentIconButton
                          larger
                          type="button"
                          style={{ color: 'green' }}
                          onClick={() => saveRow()}
                          title={'Finish editing this row.'}
                        >
                          <span className="material-symbols-rounded">done</span>
                        </TransparentIconButton>
                      </ActionsWrapper>
                    )}
                  </EditableTable.RowActions>
                </ComplexTableRow>
              ))}
            </tbody>
          )}
          {fields.length === 0 && (
            <caption style={{ captionSide: 'bottom', height: '3rem' }}>
              <div
                style={{ left: 0, bottom: 0, right: 0, position: 'absolute' }}
              >
                <h5>This table has no rows to display.</h5>
              </div>
            </caption>
          )}
        </ComplexTable>
      </TableContainer>
    </div>
  );
}

EditableTable.PreHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.margins.sm};
  gap: ${({ theme }) => theme.margins.sm};
`;

EditableTable.Title = styled.h2`
  margin: 0;
`;

EditableTable.Actions = styled.span`
  display: flex;
  gap: ${({ theme }) => theme.margins.xs};
`;

const HeaderActions = ({ children }: PropsWithChildren) => <>{children}</>;
EditableTable.HeaderActions = HeaderActions;

const TableHeader = ({ children }: PropsWithChildren) => (
  <TableHeadRow>
    <tr>{children}</tr>
  </TableHeadRow>
);
EditableTable.TableHeader = TableHeader;

const PreBodyRow = ({ children }: PropsWithChildren) => (
  <ComplexTableRow>{children}</ComplexTableRow>
);
EditableTable.PreBodyRow = PreBodyRow;

const RowActions: React.FC<{ children: ReactNode }> = ({ children }) => (
  <TableCell>{children}</TableCell>
);
EditableTable.RowActions = RowActions;

const Cell: React.FC<{
  children: ReactNode;
  reducePadding?: boolean;
  colSpan: number;
}> = ({ children, reducePadding = false, colSpan }) => (
  <TableCell colSpan={colSpan} reducePadding={reducePadding}>
    {children}
  </TableCell>
);
EditableTable.Cell = Cell;

export { EditableTable };
